import { mapValues } from "lodash";
import PDFMerger from "pdf-merger-js/browser";

import {
  buildCoaPreviewPayload,
  fetchCoaPreviewPdf,
} from "../../../../Common/Template/coaPreviewHelper";
import { TEMPLATE_SIDES } from "../../../../Common/Template/TemplateContext";

import { downloadFileFromS3WithPresigned, getFileFromS3WithPresigned } from "../../../../../utils/helpers";

import { getEnvReportHistory } from "../../../../../actions/envAnalytics";
import { getTemplates } from "../../../../../actions/productTemplate";
import { getProductFields } from "../../../../../actions/reports";

const getFileFromAWS = async (fileTemp) => {
  const privateS3Bucket = fileTemp.includes("COAs/");
  const fileName = fileTemp.split("/").pop();
  const fileBlobObj = await getFileFromS3WithPresigned([fileTemp], privateS3Bucket ? "private" : "public");
  if (fileBlobObj?.[fileTemp]) {
    return { fileName, file: fileBlobObj[fileTemp] };
  }
  return null;
};

const mergePdfFiles = async (fileBlobs) => {
  if (fileBlobs.length > 1) {
    const merger = new PDFMerger();
    const fileNames = [];
    await Promise.all(fileBlobs.map(async ({ fileName, file }) => {
      fileNames.push(fileName);
      return merger.add(file);
    }));
    const mergedPdf = await merger.saveAsBlob();
    return {
      url: window.URL.createObjectURL(mergedPdf),
      file: mergedPdf,
      name: fileNames.join(),
    };
  }
  if (fileBlobs.length === 1) {
    const { fileName, file } = fileBlobs[0];
    return {
      url: window.URL.createObjectURL(file),
      file,
      name: fileName,
    };
  }
  return { url: "", file: null, name: "" };
};

export const normalizeEnvReportPathList = (pathList) => {
  if (!pathList) {
    return [];
  }
  if (Array.isArray(pathList)) {
    return pathList.filter((item) => typeof item === "string" && item.trim().length > 0);
  }
  if (typeof pathList === "string") {
    return pathList.trim() ? [pathList] : [];
  }
  if (typeof pathList === "object") {
    return Object.values(pathList).flatMap((value) => normalizeEnvReportPathList(value));
  }
  return [];
};

/**
 * Convert an envreporthistory/ file into CoaPreviewModal's date-keyed filePaths shape.
 */
export const buildEnvHistoryCoaFilePaths = (file) => {
  const s3Paths = normalizeEnvReportPathList(file?.s3_report_path);
  if (!s3Paths.length) {
    return null;
  }

  const dateKey = file?.received_on || file?.received_date || "report";
  return { [dateKey]: s3Paths };
};

export const getEnvCoaPathEntries = (filePath, activeDate) => {
  if (!filePath || typeof filePath !== "object") {
    return [];
  }
  if (activeDate && filePath[activeDate]) {
    return [[activeDate, filePath[activeDate]]];
  }
  return Object.entries(filePath);
};

const fetchPreviewForPaths = async (date, s3Paths) => {
  const fileTempArr = Array.from(new Set(normalizeEnvReportPathList(s3Paths)));
  if (!fileTempArr.length) {
    return null;
  }
  const allPreviewFiles = await Promise.all(fileTempArr.map((fileTemp) => getFileFromAWS(fileTemp)));
  const successfullyFetched = allPreviewFiles.filter((file) => file !== null);
  if (!successfullyFetched.length) {
    return null;
  }
  const merged = await mergePdfFiles(successfullyFetched);
  return {
    url: merged.url,
    file: merged.file,
    name: merged.name,
    date,
    s3Paths: fileTempArr,
  };
};

const fetchHistoryCoaPaths = async (report, signal) => {
  const { success, paths: historyFiles, canceled } = await getEnvReportHistory(report, false, signal);
  if (!success || canceled || !historyFiles?.length) {
    return [];
  }
  const regularReport = historyFiles.find((file) => file.report_type !== "partial") || historyFiles[0];
  return normalizeEnvReportPathList(regularReport?.s3_report_path);
};

export const fetchDefaultCoaPreviewList = async ({
  filePath,
  activeDate,
  report,
  signal,
}) => {
  const entries = getEnvCoaPathEntries(filePath, activeDate);
  const entryResults = await Promise.all(
    entries.map(([date, pathList]) => fetchPreviewForPaths(date, pathList)),
  );
  let previews = entryResults.filter(Boolean);
  let resolvedS3Paths = previews.flatMap((item) => item.s3Paths);

  if (!previews.length && report) {
    const historyPaths = await fetchHistoryCoaPaths(report, signal);
    if (historyPaths.length) {
      const historyPreview = await fetchPreviewForPaths(activeDate || "report", historyPaths);
      if (historyPreview) {
        previews = [historyPreview];
        resolvedS3Paths = historyPreview.s3Paths;
      }
    }
  }

  if (!previews.length) {
    throw new Error("Preview not available.");
  }

  return {
    previews: previews.map(({ s3Paths, ...preview }) => preview),
    s3Paths: resolvedS3Paths,
  };
};

export const fetchEnvironmentTemplates = async () => {
  const response = await getTemplates({ side: TEMPLATE_SIDES.ENVIRONMENTAL });
  if (!response?.success) {
    return [];
  }
  return response.result || [];
};

export const fetchEnvironmentTemplateDetail = async (templateId, signal) => {
  const response = await getTemplates({
    template_id: templateId,
    side: TEMPLATE_SIDES.ENVIRONMENTAL,
  }, signal);
  if (signal?.aborted || response?.cancel) {
    return null;
  }
  if (!response?.success || !response.result?.[0]) {
    return null;
  }
  return response.result[0];
};

const resolveActiveSpecDate = (specsData, activeDate) => {
  if (!specsData || typeof specsData !== "object") {
    return null;
  }
  if (activeDate && specsData[activeDate]) {
    return activeDate;
  }
  const sortedDates = Object.keys(specsData).sort((a, b) => Date.parse(b) - Date.parse(a));
  return sortedDates[0] || null;
};

const isEligibleCoaSpecRow = (row = {}) => {
  const {
    test, test_category, test_spec_flag, test_result, message,
  } = row;
  if (!test || test_spec_flag === "0" || !test_result) {
    return false;
  }
  if (message?.trim()) {
    return false;
  }
  if (test_category === "MRL") {
    return false;
  }
  return true;
};

const normalizeTestResult = (row = {}) => ({
  test: row.test || "-",
  test_method: row.test_method || "-",
  test_spec: row.test_spec || "-",
  test_result: row.test_result || "-",
  test_spec_flag: row.test_spec_flag || "",
});

export const buildDataFieldsFromReport = (report = {}) => {
  const dataFields = {};
  Object.entries(report).forEach(([fieldName, fieldValue]) => {
    if (typeof fieldValue === "string" && fieldValue.length > 0) {
      dataFields[fieldName] = [fieldValue];
    }
  });
  return dataFields;
};

export const buildSectionsForPdf = (template, dataFields, productFieldMap) => {
  const sectionsArray = [];
  (template?.sections || []).forEach((section) => {
    const inputLeft = [];
    const inputRight = [];
    (section.fields || []).forEach(({
      value, size, position, flag,
    }) => {
      const sectionPart = position?.[0] === 1 ? "left" : "right";
      const sectionPartArr = sectionPart === "left" ? inputLeft : inputRight;
      const type = `${size === "large" ? "large" : "small"}TextBox`;
      if (flag === "0") {
        sectionPartArr.push({
          type, fieldName: "", value: value || "-", flag,
        });
      } else if (flag === "1") {
        const fieldName = productFieldMap?.[value] || value;
        const fieldVal = dataFields[value]?.[0] || "-";
        sectionPartArr.push({
          type, fieldName, value: fieldVal, flag,
        });
      } else {
        sectionPartArr.push({
          type, fieldName: value, value: dataFields[value]?.[0] || "-", flag,
        });
      }
    });
    if (inputLeft.length > 0 || inputRight.length > 0) {
      sectionsArray.push({
        title: section.section_name || "Section",
        inputs: { left: inputLeft, right: inputRight },
      });
    }
  });
  return sectionsArray;
};

/**
 * Build sample/test rows from backend report_result (getEnvSpec), using the same
 * eligibility rules as Generate Partial — not the full live results table.
 */
export const buildCoaSampleDataFromBackendSpecs = (report, specsData, activeDate) => {
  const date = resolveActiveSpecDate(specsData, activeDate);
  const testResults = (specsData?.[date] || [])
    .filter(isEligibleCoaSpecRow)
    .map(normalizeTestResult);

  return [{
    sample_id: report?.sample_id || "-",
    sample_type: report?.sample_type || "",
    received_date: report?.received_date || "",
    test_results: testResults,
  }];
};

/**
 * Apply selected template styling to backend-saved report + spec data via /coa/preview/.
 * Default COA continues to use S3 report_path.
 */
export const generateEnvCoaPdfBlob = async (params) => {
  const samples = buildCoaSampleDataFromBackendSpecs(
    params.report,
    params.specsData,
    params.activeDate,
  );
  if (!samples[0]?.test_results?.length) {
    throw new Error("No eligible tests available to generate template COA.");
  }

  const builderPayload = {
    company_name: params.template?.template_company_name || params.template?.company_name || "",
    company_address: params.template?.company_address || "",
    phone_number: params.template?.phone_number || "",
    fax_number: params.template?.fax_number || "",
    signed_by: params.template?.signed_by || "",
    designation: params.template?.designation || "",
    statement_inputs: params.template?.statement_inputs || [{ title: "signature", value: "", size: "" }],
    sections: buildSectionsForPdf(
      params.template,
      buildDataFieldsFromReport(params.report),
      params.productFieldMap,
    ),
    samples,
    alignment: params.template?.alignment || "Left",
  };

  const previewPayload = buildCoaPreviewPayload({
    builderPayload,
    side: TEMPLATE_SIDES.ENVIRONMENTAL,
    templateId: params.template?.template_id,
    uploadToS3: false,
    isCustomizingCoa: true,
    enableTests: false,
    logoImageUrl: params.template?.logo_image_url,
    signatureImageUrl: params.template?.signature_image_url,
  });

  const previewResult = await fetchCoaPreviewPdf(previewPayload, params.signal);
  if (previewResult.canceled || params.signal?.aborted) {
    return null;
  }
  if (!previewResult.blob) {
    throw new Error("Failed to generate template preview.");
  }
  return previewResult.blob;
};

export const getEnvironmentProductFieldMap = async () => {
  const resp = await getProductFields(false, { side: TEMPLATE_SIDES.ENVIRONMENTAL });
  if (!resp?.success) {
    return {};
  }
  return mapValues(resp.fieldsMap, (val) => val.title_field);
};

export const fetchEnvCoaPreviewFromTemplate = async ({
  templateId,
  report,
  specsData,
  activeDate,
  signal,
}) => {
  try {
    const template = await fetchEnvironmentTemplateDetail(templateId, signal);
    if (signal?.aborted) {
      return { success: false, canceled: true };
    }
    if (!template) {
      return {
        success: false,
        message: "Could not fetch template.",
      };
    }

    const productFieldMap = await getEnvironmentProductFieldMap();
    if (signal?.aborted) {
      return { success: false, canceled: true };
    }

    const blob = await generateEnvCoaPdfBlob({
      template,
      report,
      specsData,
      activeDate,
      productFieldMap,
      signal,
    });
    if (signal?.aborted) {
      return { success: false, canceled: true };
    }
    if (!blob) {
      return {
        success: false,
        message: "Failed to generate template preview.",
      };
    }

    return { success: true, blob };
  } catch (error) {
    if (signal?.aborted) {
      return { success: false, canceled: true };
    }
    return {
      success: false,
      message: error.message || "Something went wrong.",
    };
  }
};

export const downloadCoaFromS3Paths = async (s3Paths) => {
  const fileTempArr = Array.from(new Set(normalizeEnvReportPathList(s3Paths)));
  let totalFiles = 0;
  let filesDownloaded = 0;

  await Promise.all(fileTempArr.map(async (fileTemp) => {
    totalFiles += 1;
    const fileName = fileTemp.split("/").pop();
    const isPrivateS3Bucket = fileTemp.includes("COAs/");
    const success = await downloadFileFromS3WithPresigned(
      [fileTemp],
      isPrivateS3Bucket ? "private" : "public",
      [fileName],
    );
    if (success) {
      filesDownloaded += 1;
    }
  }));

  return { totalFiles, filesDownloaded };
};
